import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@uiw/react-markdown-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import 'video.js/dist/video-js.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { Provider } from 'react-redux';
import { store } from 'Rdx/store';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { getPersistor } from '@rematch/persist';
import { initializeSentry } from './sentry';
import { AuthProvider } from '@descope/react-sdk';

const persistor = getPersistor();

initializeSentry();

type SameSiteOption = 'Strict' | 'Lax' | 'None';

const isTruthy = (value: string): boolean => ['true', '1', 'yes'].includes(value.toLowerCase());

const descopeProjectId = process.env.DESCOPE_PROJECT_ID || process.env.REACT_APP_DESCOPE_PROJECT_ID || '';
const descopeBaseUrl = process.env.DESCOPE_BASE_URL || process.env.REACT_APP_DESCOPE_BASE_URL || '';
const isDescopeEnabled = ['true', '1', 'yes'].includes(
    (process.env.ENABLE_DESCOPE_AUTH || process.env.REACT_APP_ENABLE_DESCOPE_AUTH || '').toLowerCase(),
);
const descopeSessionTokenViaCookieSetting =
    process.env.DESCOPE_SESSION_TOKEN_VIA_COOKIE || process.env.REACT_APP_DESCOPE_SESSION_TOKEN_VIA_COOKIE || '';
const useDescopeSessionTokenCookie =
    descopeSessionTokenViaCookieSetting === '' ? true : isTruthy(descopeSessionTokenViaCookieSetting);
const descopeSessionCookieName =
    process.env.DESCOPE_SESSION_COOKIE_NAME || process.env.REACT_APP_DESCOPE_SESSION_COOKIE_NAME || '';
const descopeCookieDomain = process.env.DESCOPE_COOKIE_DOMAIN || process.env.REACT_APP_DESCOPE_COOKIE_DOMAIN || '';
const descopeCookieSameSiteSetting =
    process.env.DESCOPE_COOKIE_SAMESITE || process.env.REACT_APP_DESCOPE_COOKIE_SAMESITE || '';
const descopeCookieSecureSetting =
    process.env.DESCOPE_COOKIE_SECURE || process.env.REACT_APP_DESCOPE_COOKIE_SECURE || '';
const descopeRefreshCookieName =
    process.env.DESCOPE_REFRESH_COOKIE_NAME || process.env.REACT_APP_DESCOPE_REFRESH_COOKIE_NAME || '';
const hasCustomCookieSettings =
    descopeSessionCookieName !== '' ||
    descopeCookieDomain !== '' ||
    descopeCookieSameSiteSetting !== '' ||
    descopeCookieSecureSetting !== '';
const cookieSameSiteValue =
    descopeCookieSameSiteSetting === 'Strict' ||
    descopeCookieSameSiteSetting === 'Lax' ||
    descopeCookieSameSiteSetting === 'None'
        ? (descopeCookieSameSiteSetting as SameSiteOption)
        : undefined;
const cookieSecureValue = descopeCookieSecureSetting === '' ? undefined : isTruthy(descopeCookieSecureSetting);
const descopeSessionCookieConfig = hasCustomCookieSettings
    ? {
          cookieName: descopeSessionCookieName || undefined,
          domain: descopeCookieDomain || undefined,
          sameSite: cookieSameSiteValue,
          secure: cookieSecureValue,
      }
    : undefined;
const sessionTokenViaCookieProp = !useDescopeSessionTokenCookie ? false : (descopeSessionCookieConfig ?? true);

const appTree = (
    <Provider store={store}>
        <PersistGate persistor={persistor}>
            <App />
        </PersistGate>
    </Provider>
);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement)?.render(
    <React.StrictMode>
        {isDescopeEnabled && descopeProjectId ? (
            <AuthProvider
                projectId={descopeProjectId}
                baseUrl={descopeBaseUrl || undefined}
                autoRefresh
                persistTokens
                sessionTokenViaCookie={sessionTokenViaCookieProp}
                refreshTokenViaCookie={useDescopeSessionTokenCookie}
                refreshCookieName={descopeRefreshCookieName || undefined}
            >
                {appTree}
            </AuthProvider>
        ) : (
            appTree
        )}
    </React.StrictMode>,
);
serviceWorker.unregister();
